import { Button, HueLogoTile } from '@hue/design-system';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface AppErrorBoundaryProps {
  children: ReactNode;
  onReload?: () => void;
}

interface AppErrorBoundaryState {
  failed: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  public override state: AppErrorBoundaryState = { failed: false };

  public static getDerivedStateFromError(): AppErrorBoundaryState {
    return { failed: true };
  }

  public override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Falha inesperada na interface.', { error, componentStack: info.componentStack });
  }

  public override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <main className="grid min-h-screen place-items-center bg-surface px-5 py-10 text-content">
        <section className="w-full max-w-lg rounded-2xl border border-surface-border bg-surface-card p-6 shadow-card sm:p-8">
          <div className="mb-6 flex items-center gap-3">
            <HueLogoTile className="size-11 rounded-xl" />
            <div>
              <strong className="block text-lg font-bold tracking-[-.03em]">HUE•System</strong>
              <span className="text-xs text-content-muted">Recuperação da interface</span>
            </div>
          </div>
          <span className="mb-4 grid size-11 place-items-center rounded-xl bg-danger/12 text-danger">
            <AlertTriangle className="size-5" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-bold tracking-[-.04em]">
            Não foi possível exibir esta tela
          </h1>
          <p className="mt-3 text-sm leading-6 text-content-secondary">
            Seus dados já enviados foram preservados. Recarregue a aplicação para continuar com
            segurança.
          </p>
          <Button className="mt-7 w-full sm:w-auto" onClick={this.reload}>
            <RotateCcw className="size-4" aria-hidden="true" />
            Recarregar aplicação
          </Button>
        </section>
      </main>
    );
  }

  private readonly reload = (): void => {
    if (this.props.onReload) {
      this.props.onReload();
      return;
    }
    window.location.reload();
  };
}
